import { useEffect, useState, useCallback } from "react";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsLeftRight, SignIn, UsersThree, Path, Trash, Plus, FloppyDisk, Brain, TrendUp, TrendDown, Minus, CalendarBlank } from "@phosphor-icons/react";

function Stat({ label, value, sub, color, Icon }) {
  return (
    <div className="bg-[#121214] border border-white/10 p-4" data-testid={`ff-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-[#71717A]">{label}</div>
        {Icon && <Icon size={16} weight="bold" style={{ color }} />}
      </div>
      <div className="font-mono-data text-3xl" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-[#71717A] mt-1 font-mono-data">{sub}</div>}
    </div>
  );
}

export default function Customers() {
  const [data, setData] = useState([]);
  const [report, setReport] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [camId, setCamId] = useState("");
  const [lineOn, setLineOn] = useState(false);
  const [orient, setOrient] = useState("horizontal");
  const [pos, setPos] = useState(50);
  const [zones, setZones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [frame, setFrame] = useState(null);
  const [period, setPeriod] = useState("day");
  const [trends, setTrends] = useState(null);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    api.get(`/analytics/customers?period=${period}`).then((r) => setTrends(r.data)).catch(() => {});
  }, [period]);
  useEffect(() => {
    api.get("/analytics/customer-insights").then((r) => setInsights(r.data)).catch(() => {});
  }, []);

  const loadReport = useCallback(() => {
    api.get("/interactions").then((r) => setData(r.data)).catch(() => {});
    api.get("/footfall/report").then((r) => setReport(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadReport(); const t = setInterval(loadReport, 8000); return () => clearInterval(t); }, [loadReport]);
  useEffect(() => { api.get("/cameras").then((r) => { setCameras(r.data || []); if ((r.data || []).length && !camId) setCamId(r.data[0].id); }).catch(() => {}); }, []); // eslint-disable-line

  const loadCfg = useCallback((id) => {
    if (!id) return;
    api.get(`/cameras/${id}/counting`).then((r) => {
      const c = r.data || {};
      setLineOn(!!c.line_enabled);
      setOrient(c.orientation || "horizontal");
      setPos(Math.round((c.position ?? 0.5) * 100));
      const z = Object.entries(c.zones || {}).map(([name, poly]) => {
        const xs = poly.map((p) => p[0]); const ys = poly.map((p) => p[1]);
        const x = Math.min(...xs), y = Math.min(...ys);
        return { name, x: Math.round(x * 100), y: Math.round(y * 100), w: Math.round((Math.max(...xs) - x) * 100), h: Math.round((Math.max(...ys) - y) * 100) };
      });
      setZones(z);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadCfg(camId); }, [camId, loadCfg]);
  useEffect(() => {
    if (!camId) { setFrame(null); return; }
    const grab = () => api.get(`/analytics/heatmap/${camId}`).then((r) => setFrame(r.data?.frame_b64 || null)).catch(() => setFrame(null));
    grab(); const t = setInterval(grab, 10000); return () => clearInterval(t);
  }, [camId]);

  const addZone = () => setZones((z) => [...z, { name: `Zone ${z.length + 1}`, x: 30, y: 30, w: 40, h: 40 }]);
  const updateZone = (i, patch) => setZones((z) => z.map((zz, idx) => (idx === i ? { ...zz, ...patch } : zz)));
  const removeZone = (i) => setZones((z) => z.filter((_, idx) => idx !== i));

  const saveCfg = async () => {
    if (!camId) return toast.error("Pick a camera");
    setSaving(true);
    try {
      const zObj = {};
      zones.forEach((z) => {
        const x = z.x / 100, y = z.y / 100, w = z.w / 100, h = z.h / 100;
        zObj[z.name || "Zone"] = [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
      });
      await api.post(`/cameras/${camId}/counting`, { line_enabled: lineOn, orientation: orient, position: pos / 100, zones: zObj });
      toast.success("Counting setup saved — the edge agent picks it up within seconds");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const totalInteractions = data.reduce((s, d) => s + d.count, 0);
  const avgWait = data.length ? Math.round(data.reduce((s, d) => s + d.wait_seconds, 0) / data.length) : 0;
  const avgQuality = data.length ? Math.round(data.reduce((s, d) => s + d.quality, 0) / data.length) : 0;
  const hourly = report?.hourly || [];

  return (
    <div className="space-y-4">
      {/* Customer trends — Day / Week / Month + AI pattern insights */}
      <div className="bg-[#121214] border border-white/10 p-4" data-testid="customer-trends">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
            <CalendarBlank size={16} weight="bold" className="text-[#0A84FF]" /> Customer Trends
          </h2>
          <div className="flex gap-1" data-testid="trend-period-toggle">
            {["day", "week", "month"].map((p) => (
              <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
                className={`text-[11px] font-mono-data uppercase tracking-wider px-3 py-1.5 border transition-colors ${period === p ? "bg-[#0A84FF]/20 text-[#0A84FF] border-[#0A84FF]/50" : "border-white/10 text-[#71717A] hover:text-white"}`}>{p}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Stat label="Walk-ins" value={trends?.totals?.entered ?? 0} sub={`${trends?.totals?.days ?? 0} day(s)`} color="#32D74B" Icon={SignIn} />
          <Stat label="Peak" value={trends?.totals?.peak ?? 0} sub="Most at once" color="#0A84FF" Icon={UsersThree} />
          <Stat label="Busiest Day" value={insights?.busiest_day ?? "—"} sub={insights?.busiest_day ? "Weekday avg" : "Builds over time"} color="#BF5AF2" />
          <Stat label="Busiest Hour" value={insights?.busiest_hour ?? trends?.busiest_hour ?? "—"} sub={(insights?.busiest_hour || trends?.busiest_hour) ? "Highest avg" : "Builds over time"} color="#64D2FF" />
        </div>
        {trends?.series?.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trends.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1E" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
              <YAxis tick={{ fill: "#71717A", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#1A1A1E", border: "1px solid #27272A", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
              <Bar dataKey="entered" name="In" fill="#32D74B" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-xs text-[#71717A] font-mono-data py-8 text-center">Clean daily counting starts when the store opens. Walk-in history builds here day by day.</div>
        )}
        {/* AI pattern insights */}
        <div className="mt-4 border-t border-white/10 pt-4" data-testid="ai-insights">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={15} weight="duotone" className="text-[#BF5AF2]" />
            <span className="text-xs uppercase tracking-widest text-[#71717A]">AI pattern read</span>
            {insights?.trend && (
              <span className={`flex items-center gap-1 text-[11px] font-mono-data px-1.5 py-0.5 border ${insights.trend === "rising" ? "text-[#32D74B] border-[#32D74B]/40 bg-[#32D74B]/10" : insights.trend === "declining" ? "text-[#FF375F] border-[#FF375F]/40 bg-[#FF375F]/10" : "text-[#71717A] border-white/10"}`}>
                {insights.trend === "rising" ? <TrendUp size={11} weight="bold" /> : insights.trend === "declining" ? <TrendDown size={11} weight="bold" /> : <Minus size={11} weight="bold" />} {insights.trend}
              </span>
            )}
          </div>
          {insights?.summary ? (
            <div className="text-[13px] text-[#D4D4D8] leading-relaxed whitespace-pre-line font-sans">{insights.summary}</div>
          ) : (
            <p className="text-xs text-[#71717A] font-mono-data">AI pattern insights refresh overnight. Once a few days of walk-ins accumulate, busiest days/hours and the trend summary appear here.</p>
          )}
        </div>
      </div>

      {/* Footfall report */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Entered Today" value={report?.entered_today ?? 0} sub="Walked in" color="#32D74B" Icon={SignIn} />
        <Stat label="People Now" value={report?.people_now ?? 0} sub="In store right now" color="#BF5AF2" Icon={UsersThree} />
        <Stat label="Peak Today" value={report?.peak_today ?? 0} sub="Most at once" color="#0A84FF" Icon={ArrowsLeftRight} />
      </div>

      <div className="bg-[#121214] border border-white/10 p-4" data-testid="footfall-hourly">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-4 flex items-center gap-2"><ArrowsLeftRight size={16} weight="bold" className="text-[#32D74B]" /> Walk-ins by Hour — Today</h2>
        {hourly.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={hourly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1E" vertical={false} />
              <XAxis dataKey="hour" tick={{ fill: "#71717A", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
              <YAxis tick={{ fill: "#71717A", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#1A1A1E", border: "1px solid #27272A", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
              <Bar dataKey="entered" name="In" fill="#32D74B" radius={[2, 2, 0, 0]} />
              <Bar dataKey="peak" name="Peak" fill="#0A84FF" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-xs text-[#71717A] font-mono-data py-8 text-center">No footfall recorded yet today. Turn on the door line below and run the edge agent — counts appear here through the day.</div>
        )}
      </div>

      {/* Counting setup */}
      <div className="bg-[#121214] border border-white/10 p-4" data-testid="counting-setup">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1 flex items-center gap-2"><Path size={16} weight="bold" className="text-[#BF5AF2]" /> People-Counting Setup</h2>
        <p className="text-xs text-[#A1A1AA] mb-3">Draw a door line to count people walking in vs out, and mark zones (checkout, aisles) to see how many are in each area. Saved settings are pushed to your edge agent automatically.</p>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select data-testid="counting-camera-select" value={camId} onChange={(e) => setCamId(e.target.value)} className="bg-[#1A1A1E] border border-white/10 text-xs text-white px-2 py-1.5 font-mono-data outline-none focus:border-[#BF5AF2]">
            {cameras.length === 0 && <option value="">No cameras</option>}
            {cameras.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button data-testid="counting-save-btn" onClick={saveCfg} disabled={saving || !camId} className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40 hover:bg-[#32D74B]/25 disabled:opacity-40 transition-colors">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save Setup"}
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Door line */}
          <div className="border border-white/10 p-3 space-y-3">
            <label className="flex items-center gap-2 text-xs font-mono-data uppercase tracking-wider text-[#A1A1AA] cursor-pointer">
              <input data-testid="door-line-toggle" type="checkbox" checked={lineOn} onChange={(e) => setLineOn(e.target.checked)} className="w-3.5 h-3.5 accent-[#BF5AF2]" /> Door / Entry Line
            </label>
            {lineOn && (
              <>
                <div className="flex gap-2">
                  {["horizontal", "vertical"].map((o) => (
                    <button key={o} data-testid={`line-orient-${o}`} onClick={() => setOrient(o)} className={`flex-1 text-[11px] font-mono-data uppercase tracking-wider py-1.5 border transition-colors ${orient === o ? "bg-[#BF5AF2]/20 text-[#BF5AF2] border-[#BF5AF2]/50" : "border-white/10 text-[#71717A] hover:text-white"}`}>{o}</button>
                  ))}
                </div>
                <div>
                  <div className="flex justify-between text-[10px] font-mono-data text-[#71717A] mb-1"><span>Position</span><span>{pos}%</span></div>
                  <input data-testid="line-position" type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))} className="w-full accent-[#BF5AF2]" />
                </div>
                <div className="relative w-full aspect-video bg-[#0A0A0C] border border-white/10 overflow-hidden" data-testid="door-line-preview">
                  {frame
                    ? <img src={`data:image/jpeg;base64,${frame}`} alt="entrance camera" className="absolute inset-0 w-full h-full object-cover opacity-80" />
                    : <span className="absolute top-1 left-1 text-[9px] font-mono-data text-[#71717A]">waiting for camera frame…</span>}
                  {orient === "horizontal"
                    ? <div className="absolute left-0 right-0 h-0.5 bg-[#BF5AF2] shadow-[0_0_6px_#BF5AF2]" style={{ top: `${pos}%` }} />
                    : <div className="absolute top-0 bottom-0 w-0.5 bg-[#BF5AF2] shadow-[0_0_6px_#BF5AF2]" style={{ left: `${pos}%` }} />}
                  <span className="absolute bottom-1 left-1 text-[9px] font-mono-data text-white/80 bg-black/50 px-1">drag Position so the line sits across the doorway</span>
                </div>
              </>
            )}
          </div>

          {/* Zones */}
          <div className="border border-white/10 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono-data uppercase tracking-wider text-[#A1A1AA]">Zones (occupancy)</span>
              <button data-testid="add-zone-btn" onClick={addZone} className="flex items-center gap-1 text-[11px] font-mono-data uppercase px-2 py-1 bg-[#0A84FF]/15 text-[#0A84FF] border border-[#0A84FF]/40 hover:bg-[#0A84FF]/25"><Plus size={12} weight="bold" /> Add</button>
            </div>
            {zones.length === 0 && <p className="text-[11px] text-[#71717A] font-mono-data py-2">No zones yet. Add one for the checkout, entrance or an aisle.</p>}
            {zones.map((z, i) => (
              <div key={i} data-testid={`zone-row-${i}`} className="flex items-center gap-1.5">
                <input value={z.name} onChange={(e) => updateZone(i, { name: e.target.value })} className="flex-1 min-w-0 bg-[#1A1A1E] border border-white/10 text-xs text-white px-2 py-1 outline-none focus:border-[#0A84FF]" />
                {["x", "y", "w", "h"].map((k) => (
                  <input key={k} title={k.toUpperCase()} type="number" min="0" max="100" value={z[k]} onChange={(e) => updateZone(i, { [k]: Number(e.target.value) })} className="w-12 bg-[#1A1A1E] border border-white/10 text-[11px] text-white px-1 py-1 font-mono-data outline-none focus:border-[#0A84FF]" />
                ))}
                <button onClick={() => removeZone(i)} className="text-[#71717A] hover:text-[#FF3B30] p-1"><Trash size={13} weight="bold" /></button>
              </div>
            ))}
            {report?.zones_now && Object.keys(report.zones_now).length > 0 && (
              <div className="pt-2 border-t border-white/10 flex flex-wrap gap-2">
                {Object.entries(report.zones_now).map(([n, c]) => (
                  <span key={n} className="font-mono-data text-[10px] px-1.5 py-0.5 bg-[#0A84FF]/15 text-[#0A84FF] border border-[#0A84FF]/40">{n}: {c} now</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Existing interaction stats + charts */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Interactions Today" value={totalInteractions} sub="Customer touchpoints" color="#fff" />
        <Stat label="Avg Wait Time" value={`${avgWait}s`} sub="Checkout queue" color="#FF9F0A" />
        <Stat label="Service Quality" value={`${avgQuality}%`} sub="AI-scored" color="#32D74B" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#121214] border border-white/10 p-4">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-4">Interaction Volume by Hour</h2>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="cInt" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#007AFF" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#007AFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1E" vertical={false} />
              <XAxis dataKey="hour" tick={{ fill: "#71717A", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
              <YAxis tick={{ fill: "#71717A", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#1A1A1E", border: "1px solid #27272A", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
              <Area type="monotone" dataKey="count" stroke="#007AFF" strokeWidth={2} fill="url(#cInt)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#121214] border border-white/10 p-4">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-4">Wait Time vs Service Quality</h2>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1E" vertical={false} />
              <XAxis dataKey="hour" tick={{ fill: "#71717A", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
              <YAxis tick={{ fill: "#71717A", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#1A1A1E", border: "1px solid #27272A", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
              <Line type="monotone" dataKey="wait_seconds" name="Wait (s)" stroke="#FF9F0A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="quality" name="Quality %" stroke="#32D74B" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-3 font-mono-data text-xs">
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#FF9F0A]" /> Wait time</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#32D74B]" /> Service quality</span>
          </div>
        </div>
      </div>
    </div>
  );
}
